// Active-workspace-id resolver. Split from the retired Supabase-scoping
// half of this module (2026-07-13, Supabase retirement sweep):
// `scopedFrom` / `insertScoped` / `updateScoped` / `realtimeScoped` /
// `WORKSPACE_SCOPED` / the `USE_MULTITENANT` re-export existed to
// workspace_id-scope Supabase governance-table queries behind a flag that
// was never flipped on for any live call site (grep confirmed zero
// importers of any of them beyond this file) — removed rather than kept
// as dead Supabase-era scaffolding. `activeWorkspaceId` stays — it's
// imported across the shell (loadAddAppExtensions, simone's AI transport,
// session persistence, extension save/share, pages/main.js).
// `hasActiveWorkspace` has no current importer either, but is kept
// (per ticket instruction) as the paired convenience predicate.

// Read the active workspace id. PREFER the drift-corrected in-org workspace
// (`root.workspace`, re-resolved by auth.js when the JWT's active_workspace_id
// points at a workspace in the PREVIOUS org) over the raw JWT claim. The claim
// lags an org switch (it re-mints async), so after switching orgs it still names
// the old org's workspace — and any consumer scoping to it (the AI's
// list_projects, conversation list, scoped reads) leaks the previous org's data.
// Fall back to the claim only during the boot window before root.workspace resolves.
// Callers must pass the root — DOMQL handlers always have `s.root`.
//
// ── WHY BOTH ID SHAPES (WS-ACTIVE-WORKSPACE-ID-SHAPE-1) ────────────────────
// `root.workspace` arrives in one of TWO shapes depending on who populated it:
// the SDK's normalised record, which carries `id`, or the raw workspace
// document, which carries Mongo's `_id` and no `id` alias. This resolver read
// `.id` ONLY — so whenever the raw document was the one on root, the first
// branch was `undefined` and every caller silently fell through to the JWT
// claim. The guard above describes a protection that, in that shape, never
// once ran.
//
// Measured live on gita.pr.symbols.app 2026-08-31: `root.workspace` had keys
// `_id, organization, slug, displayName, …` and NO `id`, so this returned the
// claim `69e54008…` (a workspace in a DIFFERENT org) while the shell was
// rendering GITA's `6a92951a…`. The AI transport scopes `sdk.ai` with this
// value, so every prompt dispatched to — and was BILLED to — the other org's
// workspace, which answered "out of AI credits (needed 49, available 0)"
// while GITA's own balance sat at 9920 untouched. The same wrong id feeds
// `list_projects`, the conversation list and the scoped reads this comment
// already warned would leak the previous org's data.
//
// `moduleAiBridge.js` already resolves both shapes (`id || _id`); this file
// carried only half of that rule. Accepting both is what makes the documented
// precedence — the tab's own workspace beats the session-global claim — real.
// Order matches the house idiom; a root that somehow carries both always
// carries the same id in each.
export const activeWorkspaceId = (root) =>
  (root && root.workspace && (root.workspace.id || root.workspace._id)) ||
  root?.jwtClaims?.active_workspace_id ||
  null

// Convenience predicate for UI gating when rendering depends on a
// concrete workspace id being present.
export const hasActiveWorkspace = (root) => !!activeWorkspaceId(root)
